import { observer } from 'mobx-react-lite'
import {
  type ColumnId,
  exportValue,
} from '@/features/jobs/list/jobs-table-columns'
import { type SearchableJob } from '@/features/jobs/list/SearchableJob'
import { type JobTicket } from '@/features/jobs/tickets/JobTicket'
import { type JobListPerspective } from '@/features/jobs/list/job-list-view'
import {
  CalendarDateCell,
  DueCell,
  EmptyCell,
  MonoTextCell,
  PrimaryCell,
  RelativeDateCell,
  StatusCell,
  TruncatedTextCell,
  WaitingOnCell,
} from './JobListCells'
import {
  type AnyJobListColumnId,
  clientName,
  dueLabel,
  statusLabel,
  waitingOn,
} from '@/features/jobs/list/job-list-columns'
import { statusGlyph } from '@/features/jobs/job-status-display'

const legacyDates: Partial<
  Record<ColumnId, (job: SearchableJob) => Date | null>
> = {
  created: (j) => j.dateCreated,
  submitted: (j) => j.dateSubmitted,
  claimed: (j) => j.dateAccepted,
  completed: (j) => (j.status === 'Closed' ? j.dateClosed : null),
  advanceAllowed: (j) => j.dateAdvanceAllowed,
}

const legacyActivity: Partial<
  Record<ColumnId, (job: SearchableJob) => Date | null>
> = {
  lastClientMessageActivity: (j) => j.dateLastClientMessageActivity,
  lastProMessageActivity: (j) => j.dateLastProMessageActivity,
  lastRelationshipManagerMessageActivity: (j) =>
    j.dateLastRelationshipManagerMessageActivity,
}

export interface JobListCellContainerProps {
  id: AnyJobListColumnId
  job: SearchableJob
  perspective: JobListPerspective
  openTicket: JobTicket | null
}

export const JobListCellContainer = observer(function JobListCellContainer({
  id,
  job,
  perspective,
  openTicket,
}: JobListCellContainerProps) {
  switch (id) {
    case 'primary': {
      const [primary, secondary] =
        perspective === 'Pro'
          ? [clientName(job) || job.displayName, job.displayName]
          : [job.displayName, job.championPro?.displayName]
      return (
        <PrimaryCell
          primary={primary}
          secondary={secondary}
          unreadCount={job.conversation?.unreadCount ?? 0}
          openTicket={
            openTicket ? { statusMessage: openTicket.statusMessage } : null
          }
          testId={`job-title-${job.id}`}
        />
      )
    }
    case 'progress': {
      const [glyph, tone] = statusGlyph(job)
      return <StatusCell glyph={glyph} tone={tone} label={statusLabel(job)} />
    }
    case 'waitingOn': {
      const turn = waitingOn(job, perspective)
      return turn ? (
        <WaitingOnCell label={turn.label} isYou={turn.isYou} />
      ) : (
        <EmptyCell />
      )
    }
    case 'due': {
      const due = dueLabel(job, new Date())
      return due ? <DueCell {...due} /> : <EmptyCell />
    }
    case 'lastActivity':
      return <RelativeDateCell date={job.dateLastActivity} />
    case 'jobTitle':
      return (
        <TruncatedTextCell
          text={perspective === 'Pro' ? job.displayName : clientName(job)}
        />
      )
    case 'jobShortId':
      return <MonoTextCell text={job.shortId} />
  }

  const legacyDate = legacyDates[id as ColumnId]
  if (legacyDate) {
    return <CalendarDateCell date={legacyDate(job)} />
  }
  const activity = legacyActivity[id as ColumnId]
  if (activity) {
    return <RelativeDateCell date={activity(job)} />
  }

  const text = exportValue(id as ColumnId, job)
  return text ? <TruncatedTextCell text={text} /> : <EmptyCell />
})
